import { useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { formatDate } from 'lib/helpers/dateUtils';
import useLoading from 'lib/hooks/useLoading';
import useTimezone from 'lib/hooks/useTimezone';

import Avatar from 'components/shared/Avatar/Avatar';
import Button from 'components/shared/Button/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Textarea from 'components/shared/Textarea/Textarea';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import {
  MYRIAD_STAFF_LABEL,
  formatReplyTimestamp,
  getCreatorAvatarProps,
  getCreatorDisplayName
} from '../../../lib/helpers';

import Replies from './Replies';

const Comment = ({
  comment = {},
  category,
  refId,
  onDelete,
  onSave,
  isReply = false,
  parentCreatedAt
}) => {
  const textareaRef = useRef(null);
  const timezone = useTimezone();
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);

  const currentUserId = permission?.admin_id || userState?.id;
  const isViewerAdmin = !!permission?.admin_id;

  const [isEditing, setIsEditing] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [currentComment, setCurrentComment] = useState();
  const [isRepliesExpanded, setIsRepliesExpanded] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);

  const finalComment = currentComment || comment;
  const { loading, startLoading, stopLoading } = useLoading();

  const { creator, created_at, updated_at, value } = finalComment;
  const isCurrentUser = currentUserId && creator?.id === currentUserId;
  const fullName = getCreatorDisplayName({
    creator,
    isAdminAction: finalComment.created_by_type === 0,
    isViewerAdmin
  });
  const displayName = isCurrentUser ? 'You' : fullName;
  const avatarProps = getCreatorAvatarProps({
    creator,
    isMasked: fullName === MYRIAD_STAFF_LABEL && !isCurrentUser
  });
  const hasEmptyValue = !value?.trim();
  const isDisabled = !currentComment || hasEmptyValue;

  const timestamp = isReply
    ? formatReplyTimestamp(created_at, parentCreatedAt, timezone, formatDate)
    : formatDate(created_at, timezone, 'h:mma');

  const onEditHandler = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 100);
  };

  const onToggleDeleteHandler = () => setDeleteWarning((prev) => !prev);

  const commentActions = [
    { icon: 'edit-2-mono', label: 'Edit', onClick: onEditHandler },
    { icon: 'trash-mono', label: 'Delete', onClick: onToggleDeleteHandler }
  ];

  const onCancelHandler = () => {
    setIsEditing(false);
    setCurrentComment();
  };

  const onSaveHandler = async () => {
    startLoading();
    try {
      await onSave(finalComment);
      onCancelHandler();
    } finally {
      stopLoading();
    }
  };

  const handleReplyClick = () => {
    setShowReplyInput((prev) => !prev);
    setIsRepliesExpanded(!showReplyInput);
  };

  const headerRow = (
    <div className="flex min-w-0 w-full items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <Avatar
          size="small"
          className="!h-[18px] !min-h-[18px] !w-[18px] !min-w-[18px] shrink-0"
          isSquare={false}
          {...avatarProps}
        />
        <p className="truncate text-sm leading-[22px] text-neutral-600">
          <span className="font-medium text-neutral-800">{displayName} </span>
          {isReply ? 'replied:' : 'commented:'}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {!isEditing && !isReply && (
          <Button
            size="small"
            variant="link"
            text="Reply"
            onClick={handleReplyClick}
            className="!px-2"
          />
        )}

        {!isEditing && currentUserId === creator?.id && (
          <Popover
            options={commentActions}
            icon="context-menu-vertical"
            position="left"
            isFixed
            isBorder={false}
            buttonTheme="transparent"
            activeClassNames={{ button: '!bg-primary-100' }}
          />
        )}

        <Typography
          color="neutral-500"
          size="small"
          weight="normal"
          className="shrink-0 whitespace-nowrap">
          {timestamp}
          {updated_at && created_at !== updated_at ? ' (edited)' : ''}
        </Typography>
      </div>
    </div>
  );

  const editingContent = (
    <div className="flex flex-col gap-2">
      <Typography size="small">Edit comment</Typography>
      <Textarea
        allowClear
        forwardedRef={textareaRef}
        className="w-full"
        bodyClassName="w-full border-none"
        value={value}
        onChange={(e) => setCurrentComment({ ...finalComment, value: e.target.value })}
      />
      <div className="flex w-full items-center justify-end gap-3">
        <Button
          size="small"
          style="outlined"
          color="neutral"
          text="Cancel"
          onClick={onCancelHandler}
        />
        <Button
          size="small"
          text="Save"
          className={isDisabled ? '!bg-primary-100' : ''}
          isDisabled={isDisabled}
          isLoading={loading}
          onClick={onSaveHandler}
        />
      </div>
    </div>
  );

  const confirmModal = (
    <Confirm
      handleOpen={deleteWarning}
      handleClose={onToggleDeleteHandler}
      handleContinue={() => {
        onDelete(comment.id);
        onToggleDeleteHandler();
      }}
      title="Delete this comment?"
      message="You cannot undo this action"
      iconColor="danger"
      variant="danger"
      icon="message-remove-mono"
    />
  );

  if (isReply) {
    return (
      <div className="flex w-full flex-col gap-0.5">
        {headerRow}
        {isEditing ? (
          editingContent
        ) : (
          <Typography size="small" color="neutral-800" className="break-all">
            {value}
          </Typography>
        )}
        {confirmModal}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {headerRow}

      <div className="pl-5">
        {isEditing ? (
          editingContent
        ) : (
          <div className="flex flex-col gap-2 rounded bg-neutral-50 px-2 pb-3 pt-1.5">
            <Typography size="small" color="neutral-800" className="break-all">
              {value}
            </Typography>
            <Replies
              commentId={comment.id}
              repliesCount={comment.replies_count}
              parentCreatedAt={comment.created_at}
              category={category}
              refId={refId}
              isExpanded={isRepliesExpanded}
              showReplyInput={showReplyInput}
              onToggleExpanded={() => setIsRepliesExpanded((prev) => !prev)}
              onToggleReplyInput={setShowReplyInput}
            />
          </div>
        )}
      </div>

      {confirmModal}
    </div>
  );
};

export default Comment;
